/**
 * The info bubble on a calculated row: how the figure is calculated, and where to change what it
 * is calculated from. A calculated figure cannot be typed over, so the bubble says where to go.
 *
 * Hints come from the page (RowHintsProvider, a lookup by row label) or from the row itself (an
 * explicit `hint`, for rows whose label is built at run time). The bubble is drawn in a portal, so
 * a table that scrolls cannot clip it.
 */

import { createContext, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface RowHint {
  /** How the figure is calculated, in words. */
  how: string;
  /** Where to change what it is calculated from. */
  change: string;
}

type Lookup = (label: string) => RowHint | undefined;

const RowHintsContext = createContext<Lookup>(() => undefined);

export function RowHintsProvider({ lookup, children }: { lookup: Lookup; children: ReactNode }) {
  return <RowHintsContext.Provider value={lookup}>{children}</RowHintsContext.Provider>;
}

/** The row's own hint where it gives one; otherwise the page's for its label. `null` means none. */
export function useRowHint(label: string, explicit?: RowHint | null): RowHint | undefined {
  const lookup = useContext(RowHintsContext);
  if (explicit === null) return undefined;
  return explicit ?? lookup(label);
}

export function InfoBubble({ hint }: { hint: RowHint }) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const show = () => {
    const box = anchor.current?.getBoundingClientRect();
    if (box) setAt({ left: Math.min(box.left, window.innerWidth - 336), top: box.bottom + 6 });
  };
  const hide = () => setAt(null);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-label={`How this is calculated: ${hint.how} To change it: ${hint.change}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation();
          if (at) hide();
          else show();
        }}
        className="ml-1.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-gray-300 align-middle text-[10px] font-semibold not-italic leading-none text-gray-500 hover:border-accent-400 hover:text-accent-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-200"
      >
        i
      </button>
      {at &&
        createPortal(
          <div
            role="tooltip"
            style={{ position: 'fixed', left: at.left, top: at.top, width: 320 }}
            className="z-[100] whitespace-normal rounded-lg border border-gray-200 bg-white p-3 text-left text-xs font-normal not-italic leading-relaxed text-gray-700 shadow-lg"
          >
            <p>
              <span className="font-semibold text-gray-900">Calculated: </span>
              {hint.how}
            </p>
            <p className="mt-1.5">
              <span className="font-semibold text-gray-900">To change it: </span>
              {hint.change}
            </p>
          </div>,
          document.body,
        )}
    </>
  );
}

/** A row's label with its bubble, where it has one. */
export function RowLabel({ label, hint }: { label: string; hint?: RowHint | null }) {
  const found = useRowHint(label, hint);
  return (
    <span className="inline-flex items-center">
      {label}
      {found && <InfoBubble hint={found} />}
    </span>
  );
}
